import React, { useContext } from 'react';

import { MDText } from 'i18n-react';

import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { titleCaseFirstLetter } from 'Utilities';
import { PremiumOffer, Seller, TableDataRest, VehicleDetails } from 'Utilities/profiling/@types';

import { postDealershipUserUpdate } from '../../../../services/privateApi';
import { isNil } from '../../../../utilities/helpers';
import { CommonUser } from '../../../../utilities/user/@types';
import { useVehicleProfilingContext, useVehicleProfilingData } from '../DataFlow/VehicleProfilingContext.helpers';
import {
  BEREAVEMENT_KEY,
  COMPANY_KEY,
  FAMILY_KEY,
  KEEP_PRIVATE_PLATE,
  KEEPER_TYPE,
  LOCATION_FIELDS, NO_VALUE_TEXT, NONE_VALUE_TEXT, ORIGINAL_VRM, OTHER,
  PRIVATE_PLATE,
  SELLER,
  SELLING_VRM_STATUS_SKIPPED,
  SKIPPED_VALUE,
  SKIPPED_VALUE_TEXT,
  VEHICLE_NAME_KEY,
  VEHICLE_OWNER,
} from '../VehicleProfiling.constants';
import {
  formatText, getSectionList,
  IS_ELECTRIC, IS_HYBRID, isLeasedBatteryModel, isMustHaveProofOfPurchase,
  isNotEmptyLocation,
  postEnquiryVehicleData,
} from '../VehicleProfiling.helpers';
import LocalTexts from '../VehicleProfilingTexts.json';

import type { DataSection } from './Summary.helpers';
import { getDataToResend } from './Summary.helpers';

const Texts: any = LocalTexts;
const LocalT = new MDText(LocalTexts);
const OUTSTANDING_KEY = LocalT.translate('financeFigure.dpKey');
const FINANCE_KEY = LocalT.translate('finance.dpKey');
const PRIVATE_KEY = LocalT.translate('privatePlate.dpKey');

const WITHDRAWN_TITLES = ['features and equipment'];

const useSummarySections = (excludedValues: string[] = []) => {
  const { premiumOfferData } = useVehicleProfilingData();
  const { vehicleDetails } = premiumOfferData ?? {};
  const { vehicleLookupState: vehicleLookup } = useContext(VehicleLookupContext);
  const isElectric = IS_ELECTRIC(vehicleLookup);
  const isHybrid = IS_HYBRID(vehicleLookup);
  const showBatteryOwnership = isLeasedBatteryModel(vehicleLookup);
  const mustHaveProofOfPurchase = isMustHaveProofOfPurchase(vehicleDetails?.keeperStartDate);

  const sectionsObj = getSectionList({
    isElectric, isHybrid, mustHaveProofOfPurchase, showBatteryOwnership, text: LocalTexts,
  })
    .reduce<Record<string, any[]>>((acc, [key, v]) => {
      const route = v.route.split('/')[1].replace(/-/gi, ' ');
      const name: string = WITHDRAWN_TITLES.includes(route) ? '' : titleCaseFirstLetter(route);

      if (excludedValues?.includes(key)) {
        return acc;
      }

      if (key === OUTSTANDING_KEY && !vehicleDetails?.[FINANCE_KEY as keyof VehicleDetails]) {
        return acc;
      }

      acc[name] = acc[name] || [];
      acc[name].push({
        key,
        ...v,
      });

      if (
        (key === VEHICLE_OWNER && (
          vehicleDetails?.[KEEPER_TYPE as keyof VehicleDetails] === FAMILY_KEY
      || vehicleDetails?.[KEEPER_TYPE as keyof VehicleDetails] === BEREAVEMENT_KEY
      || vehicleDetails?.[KEEPER_TYPE as keyof VehicleDetails] === COMPANY_KEY
        ))
    || (key === VEHICLE_OWNER && vehicleDetails?.[KEEPER_TYPE as keyof VehicleDetails] === SELLER)) {
        acc[name].push({
          key: VEHICLE_NAME_KEY,
          ...v[VEHICLE_NAME_KEY],
        });
      }

      if (key === PRIVATE_PLATE && vehicleDetails?.[PRIVATE_KEY as keyof VehicleDetails]) {
        acc[name].push({
          key: KEEP_PRIVATE_PLATE,
          ...v[KEEP_PRIVATE_PLATE],
        });
      }

      if (key === PRIVATE_PLATE && vehicleDetails?.[PRIVATE_KEY as keyof VehicleDetails]) {
        acc[name].push({
          key: ORIGINAL_VRM,
          ...v[ORIGINAL_VRM],
        });
      }

      return acc;
    }, {});

  const sections = Object
    .keys(sectionsObj).map((title) => ({ data: sectionsObj[title], title }));

  return sections;
};

const useDisplayText = () => {
  const { userState: user } = useContext(UserContext);
  const { name: sellerAccountName } = user ?? {};
  const { premiumOfferData, sellerData } = useVehicleProfilingData();
  const { vehicleDetails } = premiumOfferData ?? {};

  const displayText = ({
    additionalKey, dpKey, format, key,
  }: TableDataRest) => {
    if (key === 'vehicleLocation') {
      if (isNotEmptyLocation(sellerData)) {
        const fieldsArr = Object.values(LOCATION_FIELDS).reduce<string[]>((acc, n) => acc.concat(n), []);
        return fieldsArr.map((fields) => sellerData?.[fields as keyof Seller]).filter(Boolean).map((val) => (
          <p key={val}>{val}</p>
        ));
      }

      return NO_VALUE_TEXT;
    }

    if (key === VEHICLE_OWNER) {
      const ownerVal = vehicleDetails?.[dpKey] === OTHER ? 'undefined' : vehicleDetails?.[dpKey];
      return formatText(Texts.vehicleOwner.questions[ownerVal as string]);
    }

    if (key === VEHICLE_NAME_KEY) {
      if (vehicleDetails?.[additionalKey] === SELLER) {
        return sellerAccountName;
      }
      return vehicleDetails?.[dpKey];
    }

    if (key === 'wheelNut') {
      return Texts.wheelNut.summaryQuestions[vehicleDetails?.[dpKey] as string] || NO_VALUE_TEXT;
    }

    if (key === 'history') {
      if (vehicleDetails?.[dpKey] === SKIPPED_VALUE) {
        return LocalT.translate('confirmDetails.sections.serviceHistoryDetails.skipped');
      }

      const valueText = Texts.servicePage.history.questions[vehicleDetails?.[dpKey] as string];

      return valueText || NO_VALUE_TEXT;
    }

    if (key === 'battery') {
      return formatText(!vehicleDetails?.[dpKey]);
    }

    if (key === PRIVATE_PLATE) {
      const valueText = Texts.privatePlate.summaryQuestions[vehicleDetails?.[dpKey] as string];

      return valueText || NO_VALUE_TEXT;
    }

    if (key === KEEP_PRIVATE_PLATE) {
      const valueText = Texts.privatePlate.keepPrivatePlate.summaryQuestions[vehicleDetails?.[dpKey] as string];

      return valueText || NO_VALUE_TEXT;
    }

    if (key === ORIGINAL_VRM) {
      const value = premiumOfferData?.[dpKey as keyof PremiumOffer];
      const additionalValue = premiumOfferData?.[additionalKey as keyof PremiumOffer];

      if (isNil(value)) {
        return NO_VALUE_TEXT;
      }

      if (additionalValue === SELLING_VRM_STATUS_SKIPPED) {
        return LocalT.translate('privatePlate.originalVRM.vrmInput.selectPage.skippedSummaryText');
      }

      return String(value).length ? value : SKIPPED_VALUE_TEXT;
    }

    if (Array.isArray(vehicleDetails?.[dpKey])) {
      const hasValues = (vehicleDetails?.[dpKey] as string[])?.filter((v) => v);

      if (!hasValues[0]) {
        return NONE_VALUE_TEXT;
      }

      return hasValues.map((v) => (
        <p key={v}>{Texts[key].selectPage?.questions?.[v] || Texts[key].questions[v]}</p>
      ));
    }

    if (format === 'split') {
      const items = (vehicleDetails?.[dpKey] as string)?.split(',').filter(Boolean).map((v) => (
        <p key={v}>{Texts[key].questions[v]}</p>
      ));

      return items?.length ? items : NONE_VALUE_TEXT;
    }

    if (format === 'questions') {
      return Texts[key].questions[vehicleDetails?.[dpKey]?.toString() as string] || NO_VALUE_TEXT;
    }

    if (format === 'money') {
      return `${vehicleDetails?.[dpKey] ? '£' : ''}${formatText(vehicleDetails?.[dpKey])}`;
    }

    if (vehicleDetails?.[additionalKey]) {
      return vehicleDetails[additionalKey];
    }

    return formatText(vehicleDetails?.[dpKey]);
  };

  return displayText;
};

const useResendVehicleDetails = (data: DataSection[]) => {
  const { premiumOfferData } = useVehicleProfilingData();
  const { sellerData } = useVehicleProfilingContext();
  const { vehicleDetails } = premiumOfferData ?? {};
  const { vehicleLookupState: vehicleLookup } = useContext(VehicleLookupContext);

  const vehicleDetailsValues = getDataToResend(data);

  const vehicleDetailsToResend = vehicleDetailsValues.reduce<Partial<VehicleDetails>>((acc, curr) => {
    acc[curr] = vehicleDetails?.[curr];
    return acc;
  }, {});

  const updateVehicleDetailsToResend = () => postEnquiryVehicleData(
    vehicleLookup?.enquiry?.dpEnquiryId,
    premiumOfferData as PremiumOffer,
    vehicleDetailsToResend,
  );

  const locationSections = Object.keys(LocalTexts?.vehicleLocation?.split?.lines || {}) as Array<keyof Pick<Seller, 'address1' | 'address2' | 'address3' | 'city'>> || [];

  const locationToResend = locationSections.reduce<Partial<CommonUser<{}>>>((acc, curr) => {
    acc[curr] = sellerData?.[curr];

    return acc;
  }, {});

  const updateLocationToResend = () => postDealershipUserUpdate(
    premiumOfferData?.sellerId,
    locationToResend,
  );

  const resendWizardDetails = async (
    { onError, onSuccess }:
    { onError: () => void; onSuccess: () => void | Promise<void>},
  ) => {
    try {
      await Promise.all([
        updateVehicleDetailsToResend(),
        updateLocationToResend(),
      ]);

      onSuccess?.();
    } catch (err) {
      window?.Sentry?.captureException(new Error(err as string));
      onError?.();
    }
  };

  return { resendWizardDetails };
};

export { useSummarySections, useDisplayText, useResendVehicleDetails };
